import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { stripeAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';

export default function StripeSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [errorMessage, setErrorMessage] = useState<string>('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');

    if (!sessionId) {
      setStatus('error');
      setErrorMessage('Missing session information');
      return;
    }

    // Confirm the checkout with our backend
    const confirmCheckout = async () => {
      try {
        await stripeAPI.confirmCheckout(sessionId);

        // Refresh user data to get updated premium status
        await refreshUser();

        setStatus('success');

        // Redirect to account page after 3 seconds
        setTimeout(() => {
          navigate('/account?upgraded=true');
        }, 3000);
      } catch (error: any) {
        console.error('Checkout confirmation error:', error);
        setStatus('error');
        setErrorMessage(error?.response?.data?.error || 'Failed to confirm your subscription. Please contact support.');
      }
    };

    confirmCheckout();
  }, [searchParams, navigate, refreshUser]);

  if (status === 'processing') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner message="Processing your payment..." />
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="rounded-2xl border border-rose-200 bg-white p-8 text-center shadow-sm">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-rose-100">
            <XCircleIcon className="h-10 w-10 text-rose-600" />
          </div>
          <h1 className="mt-6 text-2xl font-semibold text-slate-900">Payment Processing Failed</h1>
          <p className="mt-3 text-base text-slate-600">
            {errorMessage || 'There was an issue processing your payment.'}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              onClick={() => navigate('/account')}
              className="inline-flex items-center justify-center rounded-full bg-slate-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-500"
            >
              Return to Account
            </button>
            <a
              href="mailto:support@oppfoundry.com"
              className="inline-flex items-center justify-center rounded-full border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-600 shadow-sm transition hover:bg-slate-50"
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6 lg:px-8">
      <div className="rounded-2xl border border-emerald-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
          <CheckCircleIcon className="h-10 w-10 text-emerald-600" />
        </div>
        <h1 className="mt-6 text-2xl font-semibold text-slate-900">Welcome to Pro!</h1>
        <p className="mt-3 text-base text-slate-600">
          Your payment was successful and your Pro subscription is now active.
        </p>
        <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-medium text-slate-700">What's next?</p>
          <ul className="mt-3 space-y-2 text-left text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-emerald-600">✓</span>
              <span>Set up your preferences to customize your contract feed</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-emerald-600">✓</span>
              <span>Configure email notifications for new opportunities</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="mt-0.5 text-emerald-600">✓</span>
              <span>Start discovering relevant contracts faster</span>
            </li>
          </ul>
        </div>
        <p className="mt-6 text-sm text-slate-500">Redirecting to your account page...</p>
      </div>
    </div>
  );
}
